import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { toPng } from "html-to-image";
import CommentPngSkin from "@/components/social-proof/CommentPngSkin";
import type { NativeCommentViewModel } from "@/components/social-proof/nativeCommentModel";

const EXPORT_WIDTH_PX = 440;
const PAD_PX = 12;
const CAPTURE_TIMEOUT_MS = 10_000;

function safeFilePart(value: string): string {
  return (
    String(value || "x")
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "x"
  );
}

function buildFilename(model: NativeCommentViewModel): string {
  const platform = safeFilePart(model.platform || model.sourceKind || "comment");
  const idShort = safeFilePart(String(model.id || "").slice(-12) || "export");
  return `komentarz-${platform}-${idShort}.png`;
}

function waitFrames(n = 2): Promise<void> {
  return new Promise((resolve) => {
    const step = (left: number) => {
      if (left <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => step(left - 1));
    };
    step(n);
  });
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(`${label} timeout (${ms}ms)`)), ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  if (!imgs.length) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
          window.setTimeout(done, 2500);
        }),
    ),
  ).then(() => undefined);
}

/**
 * Renders an inline-styled comment skin and downloads a tight PNG
 * with transparent outer background (ready for TikTok reel overlays).
 */
export async function exportCommentPng(model: NativeCommentViewModel): Promise<void> {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    "z-index:2147483646",
    "opacity:0.01",
    "pointer-events:none",
  ].join(";");
  document.body.appendChild(host);

  const capture = document.createElement("div");
  capture.style.cssText = [
    `width:${EXPORT_WIDTH_PX}px`,
    `padding:${PAD_PX}px`,
    "box-sizing:border-box",
    "background:transparent",
  ].join(";");
  host.appendChild(capture);

  const mount = document.createElement("div");
  capture.appendChild(mount);

  const root = createRoot(mount);
  try {
    flushSync(() => {
      root.render(createElement(CommentPngSkin, { model }));
    });
    await waitFrames(2);
    await waitForImages(capture);
    await new Promise((r) => setTimeout(r, 40));

    if (!mount.firstElementChild) {
      throw new Error("Nie udało się wyrenderować skina komentarza");
    }

    const dataUrl = await withTimeout(
      toPng(capture, {
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
      }),
      CAPTURE_TIMEOUT_MS,
      "PNG capture",
    );

    if (!dataUrl || !dataUrl.startsWith("data:image/png")) {
      throw new Error("Pusty wynik PNG");
    }

    triggerDownload(dataUrl, buildFilename(model));
  } finally {
    try {
      root.unmount();
    } catch {
      /* ignore */
    }
    host.remove();
  }
}
